import { db } from '../config/firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, orderBy, onSnapshot, serverTimestamp, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { Notification, NotificationType, Task } from '../types';

let currentSubscription: (() => void) | null = null;
let notificationDebounceTimeout: NodeJS.Timeout | null = null;
let lastProcessedInvitationId: string | null = null;

export class NotificationService {
  private static notificationsRef = collection(db, 'notifications');

  static async createNotification(
    userId: string,
    recipientEmail: string,
    type: NotificationType,
    title: string,
    message: string,
    data: Record<string, any> = {}
  ): Promise<void> {
    

    const notification = {
      recipientId: userId,
      recipientEmail: recipientEmail.toLowerCase(),
      type,
      title,
      message,
      status: 'unread',
      data,
      createdAt: serverTimestamp(),
    };

    await addDoc(this.notificationsRef, notification);
   
  }

  static async markAsRead(notificationId: string): Promise<void> {
    const notificationRef = doc(this.notificationsRef, notificationId);
    await updateDoc(notificationRef, {
      status: 'read',
      readAt: serverTimestamp(),
    });
  }

  static async markAsArchived(notificationId: string): Promise<void> {
    const notificationRef = doc(this.notificationsRef, notificationId);
    await updateDoc(notificationRef, {
      status: 'archived',
      archivedAt: serverTimestamp(),
    });
  }

  static async getUnreadNotifications(userId: string): Promise<Notification[]> {
    const q = query(
      this.notificationsRef,
      where('recipientId', '==', userId),
      where('status', '==', 'unread'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Notification));
  }

  static async checkPendingInvitations(email: string): Promise<void> {
    if (!email) return;

    const invitationsRef = collection(db, 'invitations');
    const q = query(
      invitationsRef,
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    // console.log('Found pending invitations:', snapshot.size);

    if (!snapshot.empty) {
      for (const docSnapshot of snapshot.docs) {
        const invitation = docSnapshot.data();
        
        // Skip expired invitations
        if (invitation.expiresAt && new Date() > invitation.expiresAt.toDate()) {
          // console.log('Skipping expired invitation:', docSnapshot.id);
          continue;
        }

        // Check if notification already exists for this invitation
        const existingNotificationQuery = query(
          this.notificationsRef,
          where('recipientEmail', '==', email.toLowerCase()),
          where('data.invitationId', '==', docSnapshot.id),
          where('type', '==', 'project_invitation'),
          where('status', 'in', ['unread', 'read'])
        );
        const existingNotification = await getDocs(existingNotificationQuery);
      

        if (existingNotification.empty) {
          const projectRef = doc(db, 'projects', invitation.projectId);
          const projectDoc = await getDoc(projectRef);
          const project = projectDoc.data() as { title: string };

          // Get user document by email
          const usersRef = collection(db, 'users');
          const userQuery = query(usersRef, where('email', '==', email.toLowerCase()));
          const userSnapshot = await getDocs(userQuery);
          const userId = userSnapshot.docs[0]?.id || email;

         

          await this.createNotification(
            userId,
            email.toLowerCase(),
            'project_invitation',
            'Project Invitation',
            `You have been invited to join the project "${project.title}"`,
            {
              projectId: invitation.projectId,
              invitationId: docSnapshot.id,
              invitedBy: invitation.invitedBy,
              role: invitation.role,
            }
          );
        } 
      }
    }
  }

  static subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void
  ): () => void {

    // Clean up existing subscription if it exists
    if (currentSubscription) {
      console.log('Cleaning up existing subscription');
      currentSubscription();
      currentSubscription = null;
    }

    let unsubscribeNotifications: (() => void) | undefined;
    let unsubscribeInvitations: (() => void) | undefined;
    let userEmail: string | undefined;

    // First get the user's email
    getDoc(doc(db, 'users', userId)).then(async userDoc => {
      userEmail = userDoc.data()?.email?.toLowerCase();
      // console.log('Found user email:', userEmail);
      
      if (!userEmail) {
        
        return;
      }

      // Subscribe to notifications
      const notificationsQuery = query(
        this.notificationsRef,
        where('status', '==', 'unread'),
        orderBy('createdAt', 'desc')
      );

      // Subscribe to invitations
      const invitationsRef = collection(db, 'invitations');
      const invitationsQuery = query(
        invitationsRef,
        where('email', '==', userEmail),
        where('status', '==', 'pending')
      );

      // Set up real-time listener for invitations
      unsubscribeInvitations = onSnapshot(invitationsQuery, async (invitationsSnapshot) => {
        // console.log('=== Invitation snapshot update ===', {
        //   count: invitationsSnapshot.size,
        //   timestamp: new Date().toISOString()
        // });

        if (!userEmail) {
          
          return;
        }

        // Clear any existing timeout
        if (notificationDebounceTimeout) {
          clearTimeout(notificationDebounceTimeout);
        }

        // Set a new timeout to process invitations
        notificationDebounceTimeout = setTimeout(async () => {
          for (const invitationDoc of invitationsSnapshot.docs) {
            const invitation = invitationDoc.data();
            
            // Skip if we've just processed this invitation
            if (lastProcessedInvitationId === invitationDoc.id) {
              // console.log('Skipping recently processed invitation:', invitationDoc.id);
              continue;
            }
            
            if (invitation.expiresAt && new Date() > invitation.expiresAt.toDate()) {
              // console.log('Skipping expired invitation:', invitationDoc.id);
              continue;
            }

            const existingNotificationQuery = query(
              this.notificationsRef,
              where('recipientEmail', '==', userEmail),
              where('data.invitationId', '==', invitationDoc.id),
              where('type', '==', 'project_invitation'),
              where('status', 'in', ['unread', 'read'])
            );
            const existingNotification = await getDocs(existingNotificationQuery);
            console.log('Real-time invitation check:', {
              invitationId: invitationDoc.id,
              hasExisting: !existingNotification.empty,
              count: existingNotification.size
            });

            if (existingNotification.empty) {
              const projectRef = doc(db, 'projects', invitation.projectId);
              const projectDoc = await getDoc(projectRef);
              
              if (!projectDoc.exists()) {
                // console.log('Project not found:', invitation.projectId);
                continue;
              }
              
              const project = projectDoc.data() as { title: string };

              // console.log('Creating notification from real-time update:', {
              //   invitationId: invitationDoc.id,
              //   projectTitle: project.title
              // });

              await this.createNotification(
                userId,
                userEmail as string,
                'project_invitation',
                'Project Invitation',
                `You have been invited to join the project "${project.title}"`,
                {
                  projectId: invitation.projectId as string,
                  invitationId: invitationDoc.id,
                  invitedBy: invitation.invitedBy,
                  role: invitation.role,
                }
              );

              // Update last processed invitation
              lastProcessedInvitationId = invitationDoc.id;
            }
          }
        }, 500); // 500ms debounce delay
      });

      // Set up real-time listener for notifications
      unsubscribeNotifications = onSnapshot(notificationsQuery, async snapshot => {
       

        if (!userEmail) {
          callback([]);
          return;
        }

        const notificationsPromises = snapshot.docs
          .filter(doc => {
            const data = doc.data();
            return data.recipientId === userId || data.recipientEmail === userEmail;
          })
          .map(async docSnapshot => {
            const notification = {
              id: docSnapshot.id,
              ...docSnapshot.data()
            } as Notification;

            if (notification.type === 'project_invitation' && notification.data.invitationId) {
              const invitationRef = doc(db, 'invitations', notification.data.invitationId);
              const invitationDoc = await getDoc(invitationRef);
              const invitationData = invitationDoc.data();
              
              if (!invitationDoc.exists() || 
                  invitationData?.status !== 'pending' || 
                  (invitationData?.expiresAt && 
                   new Date() > (invitationData.expiresAt as any).toDate())) {
                console.log('Archiving invalid/expired notification:', notification.id);
                await this.markAsArchived(notification.id);
                return null;
              }
            }

            return notification;
          });

        const notifications = (await Promise.all(notificationsPromises))
          .filter((notif): notif is Notification => notif !== null);

   

        callback(notifications);
      });
    });

    // Create cleanup function
    const cleanup = () => {
      // console.log('Cleaning up notification subscriptions');
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
      }
      if (unsubscribeInvitations) {
        unsubscribeInvitations();
      }
      currentSubscription = null;
    };

    // Store the current subscription cleanup
    currentSubscription = cleanup;

    return cleanup;
  }

  static async createDeadlineNotification(userId: string, taskCount: number): Promise<void> {
    // Check if there's an existing deadline notification
    const existingQuery = query(
      this.notificationsRef,
      where('type', '==', 'upcoming_deadline'),
      where('recipientId', '==', userId)   // Using userId directly since it's the owner
    );

    const snapshot = await getDocs(existingQuery);

    if (snapshot.empty) {
      // Create new notification if none exists
      await addDoc(this.notificationsRef, {
        type: 'upcoming_deadline',
        title: 'Upcoming Deadlines',
        message: `${taskCount} Uncompleted tasks due soon`,
        status: 'unread',  // Using the correct status field
        userId: userId,    // This is the owner of the tasks
        recipientId: userId,    // Added this
        recipientEmail: null, 
        createdAt: serverTimestamp(),
        data: { 
          taskCount,
          redirectTo: '/deadlines'  // Add redirect path for clicking the notification
        }
      });
    } else {
      // Update existing notification
      const notificationDoc = snapshot.docs[0];
      await updateDoc(doc(db, 'notifications', notificationDoc.id), {
        message: `${taskCount} Uncompleted tasks due soon`,
        status: 'unread',  // Add this to ensure it stays unread
        data: { 
          taskCount,
          redirectTo: '/deadlines'
        }
      });
    }
  }

  static async removeDeadlineNotification(userId: string): Promise<void> {
    const existingQuery = query(
      this.notificationsRef,
      where('recipientId', '==', userId),
      where('type', '==', 'upcoming_deadline')
    );

    const snapshot = await getDocs(existingQuery);
    
    if (!snapshot.empty) {
      await deleteDoc(doc(db, 'notifications', snapshot.docs[0].id));
    }
  }

  static subscribeToDeadlines(userId: string, tasks: Task[], callback: (taskCount: number) => void) {
    // Calculate tasks due within 5 days
    const now = new Date();
    const fiveDaysFromNow = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);
    
    const dueTasks = tasks.filter(task => 
      task.deadline && 
      task.status !== 'Completed' && 
      task.deadline <= fiveDaysFromNow
    );

    callback(dueTasks.length);

    // Subscribe to task changes
    const tasksRef = collection(db, 'tasks');
    const taskQuery = query(tasksRef, where('userId', '==', userId));

    return onSnapshot(taskQuery, (snapshot) => {
      const updatedTasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Task[];

      const updatedDueTasks = updatedTasks.filter(task => 
        task.deadline && 
        task.status !== 'Completed' && 
        task.deadline <= fiveDaysFromNow
      );

      callback(updatedDueTasks.length);
    });
  }
} 